import React, {useState} from 'react';
import {Form, Formik, FormikHelpers} from "formik";
import TriggerType from "types/TriggerType";
import FormControls from "components/FormControls";
import MultiSelector from "components/Form/MultiSelector";
import {useQuery} from "react-query";
import ngage from "services/ngage";
import FieldHeader from "components/Form/FieldHeader";
import Toggle from "components/Form/Toggle";
import {Beacon} from "types/Beacon";
import {PhysicalObject} from "types/PhysicalObject";
import {QrCode} from "types/QrCode";
import {Item} from 'components/Form/MultiSelector/MultiSelector';
import HardwareType from "types/HardwareType";
import IconName from "types/IconName";
import Field from "components/Form/Field";
import Selector from "components/Form/Selector";
import Button from "components/Button";
import {DateTime, Info} from "luxon";
import {TriggerTimings} from "types/TriggerTimings";
import ActionManager from 'pages/Trigger/Form/ActionManager';
import {Option} from "components/Form/Select/Select";
import {ActionFormData} from './ActionEditor';
import TriggerActionType from "types/TriggerActionType";
import * as Yup from "yup";
import FieldHelp from "../../../components/Form/Form/Field/FieldHelp";
import {ErrorNotification} from "../../../components/Form/ErrorNotification";
import {InteractionArea} from "../../../types/InteractionArea";
import {Zone, ZoneListing, ZoneHardware} from "../../../types/Zone";
import {uniqueHardwareId} from "../Mutate/MutateTrigger";
import {toTitleCase} from "../../../services/string";
import {useOperator} from "../../../context/OperatorContext";

// Types ----

export type TriggerFormData = {
  trigger_type: TriggerType
  name: string
  is_enabled: boolean
  hardware: Array<Option>
  segment_id?: number | null
  timings: TriggerTimings
  actions: Array<ActionFormData>
}

type TriggerFormProps = {
  values?: TriggerFormData | null
  onSubmit: (values: TriggerFormData, helpers: FormikHelpers<TriggerFormData>) => any
}

// Helpers ----

const defaultTimings: TriggerTimings = {
  timezone: DateTime.now().zoneName,
  monday: {frequency: 'always'},
  tuesday: {frequency: 'always'},
  wednesday: {frequency: 'always'},
  thursday: {frequency: 'always'},
  friday: {frequency: 'always'},
  saturday: {frequency: 'always'},
  sunday: {frequency: 'always'},
}

const defaultAction: ActionFormData = {
  action_type: TriggerActionType.UNKNOWN,
  name: '',
  order: 1
}

const defaultValues: TriggerFormData = {
  trigger_type: TriggerType.ACTIVATION,
  name: '',
  hardware: [],
  segment_id: null,
  timings: {...defaultTimings},
  is_enabled: true,
  actions: [{...defaultAction}]
}

const validationSchema = Yup.object().shape({
  name: Yup.string().required("This field is required").max(250, "Maximum of 250 characters"),
  hardware: Yup.array().of(Yup.object()).min(1, "This field is required").required('This field is required'),
  actions: Yup.array().of(Yup.object().shape({
    action_type: Yup.string().notOneOf([TriggerActionType.UNKNOWN], "This field is required").required("This field is required"),
    name: Yup.string().required("This field is required").max(250, "Maximum of 250 characters"),
    entity: Yup.mixed().when('action_type', ([actionType]: any) => {
      if (actionType === TriggerActionType.URL) {
        return Yup.string()
          .matches(
            /^https?:\/\//,
            'URL must start with http:// or https://'
          )
          .max(250, "Maximum of 250 characters")
          .url('URL is not valid')
          .required("This field is required");
      } else {
        return Yup.object().required("This field is required");
      }
    })
  }))
})

export function hardwareToSelectItem(hardwareItem: Beacon | PhysicalObject | QrCode | InteractionArea | ZoneHardware): Item {
  const typeIcons = {
    [HardwareType.BEACON]: IconName.WIFI,
    [HardwareType.PHYSICAL_OBJECT]: IconName.CUBE,
    [HardwareType.QR_CODE]: IconName.QR_CODE,
    [HardwareType.INTERACTION_AREA]: IconName.LOCATION_MARKER,
    [HardwareType.ZONE]: IconName.CUBE_TRANSPARENT,
  }
  let name: string = hardwareItem.name;

  switch(hardwareItem.type) {
    case HardwareType.BEACON:
      name = `${name} (${hardwareItem.site.name})`
      break;
    case HardwareType.QR_CODE:
      name = `${name}`
      break;
    case HardwareType.INTERACTION_AREA:
      name = `${name} (${hardwareItem.site?.name})`
      break;
    case HardwareType.ZONE:
      name = `${name} (${hardwareItem.site.name})`
      break;
    default:
      name = `${name}`
  }

  return {
    id: uniqueHardwareId(hardwareItem),
    name,
    description: toTitleCase(hardwareItem.type),
    icon: typeIcons[hardwareItem.type]
  }
}

// Component ----

function TriggerForm({values, onSubmit}: TriggerFormProps) {
  const {termFor} = useOperator();
  const [triggerType, setTriggerType] = useState<TriggerType>(values?.trigger_type || defaultValues.trigger_type);

  const {data: audienceSegments, isLoading: audienceSegmentsLoading} = useQuery(
    ['audience-segments.all', {page: 1}],
    () => ngage.audienceSegments.all(1)
  )

  // Hardware queries
  const {data: beacons, isLoading: beaconsLoading} = useQuery(
    ['beacons.all', {page: 1, perPage: 1000}],
    () => ngage.beacons.all(1, 1000),
    {enabled: triggerType === TriggerType.ACTIVATION}
  )

  // const {data: physicalObjects, isLoading: physicalObjectsLoading} = useQuery(
  //   ['physical-objects.all', {page: 1, perPage: 1000}],
  //   () => ngage.physicalObjects.all(1, 1000),
  //   {enabled: triggerType === TriggerType.ACTIVATION}
  // )

  const {data: qrCodes, isLoading: qrCodesLoading} = useQuery(
    ['qr-codes.all', {page: 1, perPage: 1000}],
    () => ngage.qrCodes.all(1, 1000),
    {enabled: triggerType === TriggerType.ACTIVATION}
  )

  const {data: interactionAreas, isLoading: interactionAreasLoading} = useQuery(
    ['interaction-areas.all', {page: 1, per_page: 1000}], // TODO: Add site dynamically
    () => ngage.interactionAreas.filtered({page: 1, per_page: 1000}), // TODO: Add site dynamically
    {enabled: triggerType === TriggerType.ACTIVATION}
  )

  const {data: zones, isLoading: zonesLoading} = useQuery(
    ['zones.all', {page: 1, per_page: 1000}], // TODO: Add site dynamically
    () => ngage.zones.filtered({page: 1, per_page: 1000}), // TODO: Add site dynamically
    {enabled: triggerType === TriggerType.ACTIVATION}
  )

  // function selectType(type: TriggerType, formik: FormikHelpers<any>) {
  //   setTriggerType(type)
  //   void formik.setFieldValue('trigger_type', type)
  //   void formik.setFieldValue('hardware', [])
  // }

  function zoneToSelectItem(zone: ZoneListing): Item {
    return {
      id: `zone.${zone.id}`,
      name: `${zone.name} (${zone.site.name})`,
      description: termFor('Zone'),
      icon: IconName.CUBE_TRANSPARENT
    }
  }

  const selectableHardware: Array<Item> = [
    ...(beacons?.data.map(hardwareToSelectItem) || []),
    // ...(physicalObjects?.data.map(hardwareToSelectItem) || []),
    ...(qrCodes?.data.map(hardwareToSelectItem) || []),
    ...(interactionAreas?.data.map(hardwareToSelectItem) || []),
    ...(zones?.data.map(zoneToSelectItem) || []),
  ]

  return (
    <Formik initialValues={values || {...defaultValues}} validationSchema={validationSchema} onSubmit={onSubmit}>
      {formik => {
        return (
          <Form className="space-y-8 divide-y divide-gray-200">
            <ErrorNotification formik={formik} />

            <div className="shadow sm:rounded-md">
              <div className="px-4 py-5 bg-white sm:p-6 sm:rounded-t-md">
                <div className="space-y-8 divide-y divide-gray-200">
  
                  {/* Conditions */}
                  <div>
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Conditions
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Setup what conditions cause this trigger to execute, and when that should happen
                      </p>
                    </div>
                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
  
                      {/* Name */}
                      <div className="sm:col-span-4">
                        <Field
                          type="text"
                          label="Name *"
                          hint='250 chars max'
                          name="name"
                          id="name"
                          autoComplete="off"
                          maxLength="250"
                          autoFocus
                        />
                      </div>
  
                      {/* Type */}
                      {/*<div className="sm:col-span-6">*/}
                      {/*  <FieldHeader label="Type"/>*/}
                      {/*  <div className="mt-1">*/}
                      {/*    <ButtonGroup*/}
                      {/*      selectedId={triggerType}*/}
                      {/*      onChange={option => selectType(option.id as TriggerType, formik)}*/}
                      {/*    >*/}
                      {/*      {[*/}
                      {/*        {id: TriggerType.ACTIVATION, name: 'Activation'},*/}
                      {/*        {id: TriggerType.TIME, name: 'Time', disabled: true},*/}
                      {/*        {id: TriggerType.VISIT, name: `${termFor('Site')} Visit`, disabled: true},*/}
                      {/*      ]}*/}
                      {/*    </ButtonGroup>*/}
                      {/*  </div>*/}
                      {/*</div>*/}
  
                      {/* Hardware */}
                      <div className="sm:col-span-4 xl:col-span-3">
                        {formik.values.trigger_type === TriggerType.ACTIVATION &&
                          <>
                            <MultiSelector
                              label="Entity *"
                              hint='Select the entity that will initiate this trigger (you can select multiple)'
                              items={selectableHardware}
                              selectedIds={formik.values.trigger_type === TriggerType.ACTIVATION ? formik.values.hardware.map(h => h.id) : []}
                              isLoading={beaconsLoading || qrCodesLoading || interactionAreasLoading || zonesLoading /*|| physicalObjectsLoading */}
                              onChange={items => formik.setFieldValue('hardware', items)}
                            />

                            {(!!formik.errors.hardware && formik.touched.hardware) &&
                              <FieldHelp isError>
                                {formik.errors.hardware}
                              </FieldHelp>
                            }
                          </>
                        }
                      </div>
  
                      {/* Audience Segment */}
                      <div className="sm:col-span-4 xl:col-span-3 col-start-1 sm:col-start-1 xl:col-start-1">
                        <FieldHeader label="Audience Segment" hint="optional"/>
  
                        <div className="mt-1 flex space-x-2">
                          <div className="w-full">
                            <Selector
                              items={audienceSegments?.data.map(segment => ({
                                id: segment.id,
                                name: segment.name,
                                icon: IconName.USER_GROUP
                              })) || []}
                              selectedId={formik.values.segment_id}
                              isLoading={audienceSegmentsLoading}
                              onChange={item => formik.setFieldValue('segment_id', item.id)}
                            />
                          </div>
  
                          <Button
                            color={Button.colors.WHITE}
                            size={Button.sizes.MEDIUM}
                            disabled={!formik.values.segment_id}
                            onClick={() => formik.setFieldValue('segment_id', null)}
                          >Clear</Button>
                        </div>
                      </div>
  
                      {/* Timings */}
                      <div className="sm:col-span-6">
                        <FieldHeader label="Timings"/>
                        <span className="text-sm text-gray-500">Choose when this trigger is able to run</span>
  
                        <ul className="mt-2">
                          {Info.weekdays().map(day => {
                            const dayKey = day.toLowerCase() as keyof Omit<TriggerTimings, 'timezone'>
  
                            return (
                              <li key={day} className="mb-3 md:grid md:grid-cols-12 items-center">
                                <div className="text-gray-700 text-sm md:col-span-2 xl:col-span-1">
                                  {day}s
                                </div>
  
                                <div className="md:flex md:col-span-10 xl:col-span-11">
                                  <div className="my-1 md:my-0 md:ml-2">
                                    <Field as="select" name={`timings.${dayKey}.frequency`}>
                                      <option value="always">Always</option>
                                      <option value="never">Never</option>
                                      <option value="between">Between</option>
                                    </Field>
                                  </div>
  
                                  {formik.values.timings?.[dayKey].frequency === 'between' &&
                                    <div className="md:ml-2 flex items-center">
                                      <div className="w-1/3 md:w-full">
                                        <Field
                                          type="time"
                                          name={`timings.${dayKey}.periods.0.0`}
                                          minLength={5}
                                          maxLength={5}
                                          required
                                        />
                                      </div>
                                      <span className="mx-2">and</span>
                                      <div className="w-1/3 md:w-full">
                                        <Field
                                          type="time"
                                          name={`timings.${dayKey}.periods.0.1`}
                                          minLength={5}
                                          maxLength={5}
                                          required
                                        />
                                      </div>
                                    </div>
                                  }
                                </div>
                              </li>
                            )
                          })}
                        </ul>
                      </div>
                    </div>
                  </div>
  
                  {/* Actions */}
                  <div className="pt-8">
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Actions
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Setup what happens when this trigger is executed
                      </p>
                    </div>
                    <ActionManager name='actions'/>
                  </div>
  
                  {/* Status */}
                  <div className="pt-8">
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Status
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Enable or disable this trigger
                      </p>
                    </div>
                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                      <div className="sm:col-span-6">
                        <Toggle
                          label="Enabled"
                          checked={formik.values.is_enabled}
                          onChange={checked => formik.setFieldValue('is_enabled', checked)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
  
              <FormControls formik={formik}/>
            </div>
          </Form>
        )
      }}
    </Formik>
  );
}

TriggerForm.defaults = defaultValues


// Export ----

export default TriggerForm;